/**
 * order-row-stub — the row. What a buyer kept is not a record of a
 * transaction, it is a pile of STUBS, so the row is the physical object: one
 * paper-ground stub with a counterfoil torn off along a real perforation —
 * two notches punched clean through the card (a mask, so the page shows
 * through rather than a fake page-coloured dot), a dashed seam running
 * between them, and the counterfoil carrying the one number a stub has ever
 * carried, HOW MANY IT ADMITS.
 *
 * That is the row's argument about hierarchy: the loudest slot goes to the
 * ticket count, the second to the event, and the money drops to the stub's
 * foot beside the order reference — the register a stub actually prints its
 * price in. Someone opening this screen is counting tickets and naming a
 * night, not auditing a total.
 *
 * The paper ground is @momo/ticket-template's own pinned `.tkt-paper-ground`,
 * mounted from `ticketCardGrounds` — the same paper the shipped ticket is
 * printed on, including the darkened hairline token it re-points, which is
 * what the dashed perforation is drawn in.
 *
 * STATE IS THE STUB'S MATERIAL, NOT A TOKEN BESIDE IT. A badge says the same
 * amount whichever state it names, sits in the row's quietest tier, and asks
 * the reader to translate a word into a consequence. The stub's own
 * vocabulary is richer, and the two marked states are not two doses of one
 * thing, so they get two different KINDS of signal:
 *
 * TERMINAL (refunded, cancelled) — THE STUB IS OVERPRINTED. Four instruments:
 * the paper ground inverts to the package's own pinned INK ground, so a dead
 * stub is visibly not-paper in a pile of paper under either scheme, before a
 * word is read; the perforation closes (solid rule, no punched notches) —
 * nothing was torn off this one and nothing will be; the count is STRUCK,
 * since the one number a stub carries is how many it admits and this admits
 * nobody; and the ink inverts — every printed fact drops to a whisper so the
 * plain sentence is the only thing left at full ink, putting "these tickets
 * no longer work at the door" at the TOP of the row's ink hierarchy where the
 * badge sat at the bottom.
 *
 * TRANSIENT (payment confirming) — THE STUB IS STILL ON THE ROLL. The one
 * state where the buyer waits on US, resolving into a live ticket in seconds,
 * so it wears none of the above: full paper, full ink, nothing struck, nothing
 * closed. Its only mark is an indeterminate hairline running the stub's foot —
 * the single moving thing in the list. Motion cannot be read as damage.
 *
 * PAID wears nothing at all: paper, punched, dashed, silent.
 */

import { formatCurrency, formatDate } from "@mhmo91/rx";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { ticketCardGrounds } from "@momo/ticket-template";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import type { BoughtOrder } from "./orders.state.ts";

@schmancyElement("order-row-stub")
export class OrderRowStub extends LocalizeMixin(SchmancyElement) {
	// The notches are the one thing here no utility can express: a hole is a
	// mask, and its two circles must be punched at the same inline offset the
	// counterfoil ends at, top and bottom edge alike. Kept as one named
	// constant so the seam and the holes cannot drift apart.
	static styles = [
		css`
			:host {
				display: block;
				--stub-seam: 5.5rem;
			}
			.stub-punched {
				-webkit-mask-image: radial-gradient(
						circle 7px at var(--stub-seam) 0,
						transparent 98%,
						#000 100%
					),
					radial-gradient(
						circle 7px at var(--stub-seam) 100%,
						transparent 98%,
						#000 100%
					);
				-webkit-mask-composite: source-in;
				mask-image: radial-gradient(
						circle 7px at var(--stub-seam) 0,
						transparent 98%,
						#000 100%
					),
					radial-gradient(
						circle 7px at var(--stub-seam) 100%,
						transparent 98%,
						#000 100%
					);
				mask-composite: intersect;
			}
		`,
	];

	@property({ type: Object }) order!: BoughtOrder;

	override render() {
		const { order } = this;
		const voided = order.status === "refunded" || order.status === "cancelled";
		const note =
			order.status === "paid"
				? null
				: this.t(
						order.status === "authorised"
							? "storefront.orders.noteAuthorised"
							: order.status === "refunded"
								? "storefront.orders.noteRefunded"
								: "storefront.orders.noteCancelled",
					);
		const boughtOn =
			formatDate(order.boughtAt.toISOString(), this.locale) ?? "";
		const total = formatCurrency(order.totalCents / 100, "EUR", this.locale);
		return html`
			${ticketCardGrounds}
			<schmancy-card
				type="chromeless"
				interactive
				role="button"
				aria-label=${[
					order.eventName,
					boughtOn,
					order.ticketCount === 1
						? this.t("storefront.orders.ticketsOne")
						: this.t("storefront.orders.ticketsOther", {
								count: order.ticketCount,
							}),
					total,
					// The sentence rides the accessible name because `aria-label`
					// overrides the row's own contents: without it the one carrier
					// the design leans on is the one thing a screen reader never
					// reaches, and a dead order reads as live tickets by exactly the
					// route the visual treatment was built to close.
					note,
				]
					.filter(Boolean)
					.join(", ")}
				class="block"
			>
				<div
					class=${this.classMap({
						"grid grid-cols-[5.5rem_minmax(0,1fr)] overflow-hidden rounded-lg border border-outlineVariant": true,
						// The material IS the state: paper for a stub that still
						// admits someone, the page's own ground for one that does not.
						"tkt-paper-ground stub-punched": !voided,
						"tkt-ink-ground": voided,
					})}
				>
					<div
						class=${this.classMap({
							"flex flex-col items-center justify-center gap-1 border-r px-2 py-5 border-outlineVariant": true,
							"border-dashed": !voided,
						})}
					>
						<schmancy-typography
							type="display"
							token="sm"
							weight="bold"
							class=${this.classMap({ "line-through opacity-40": voided })}
						>
							${order.ticketCount}
						</schmancy-typography>
						<schmancy-typography
							type="label"
							token="xs"
							transform="uppercase"
							class=${this.classMap({
								"tracking-widest": true,
								"opacity-70": !voided,
								"line-through opacity-40": voided,
							})}
						>
							${
								order.ticketCount === 1
									? this.t("storefront.orders.ticketLabelOne")
									: this.t("storefront.orders.ticketLabelOther")
							}
						</schmancy-typography>
					</div>

					<div class="flex min-w-0 flex-col gap-2 px-4 py-5">
						<schmancy-typography
							type="label"
							token="xs"
							transform="uppercase"
							class=${this.classMap({
								"tracking-widest": true,
								"opacity-70": !voided,
								"opacity-40": voided,
							})}
						>
							${boughtOn}
						</schmancy-typography>
						${when(
							order.eventName === null,
							// The event read is a second document and answers after the
							// row does; the row keeps its own geometry meanwhile rather
							// than holding the whole list back for one name.
							() =>
								html`<schmancy-skeleton
									class="h-6 w-48 max-w-full rounded-lg"
								></schmancy-skeleton>`,
							() => html`
								<schmancy-typography
									type="title"
									token="md"
									weight="bold"
									maxLines="2"
									class=${this.classMap({ "opacity-55": voided })}
								>
									${order.eventName}
								</schmancy-typography>
							`,
						)}
						${when(
							note !== null,
							() => html`
								<schmancy-typography type="body" token="sm">
									${note}
								</schmancy-typography>
							`,
						)}
						<div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
							<schmancy-typography
								type="label"
								token="xs"
								class=${this.classMap({
									"font-mono uppercase": true,
									"opacity-50": !voided,
									"opacity-35": voided,
								})}
							>
								${order.orderId}
							</schmancy-typography>
							<schmancy-typography
								type="label"
								token="xs"
								class=${this.classMap({
									"opacity-70": !voided,
									"opacity-35": voided,
								})}
							>
								${total}
							</schmancy-typography>
						</div>
					</div>

					${when(
						order.status === "authorised",
						() => html`
							<schmancy-progress
								indeterminate
								size="sm"
								class="col-span-2"
								aria-hidden="true"
							></schmancy-progress>
						`,
					)}
				</div>
			</schmancy-card>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"order-row-stub": OrderRowStub;
	}
}
